import { StrictMode, Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter, Navigate, NavLink, Route, Routes, useLocation, useNavigate,
} from 'react-router-dom';
import { api, aoPerderSessao } from './api';
import { Marca } from './marca';
import { Entrar } from './telas/Entrar';
import { Acesso, segredoDoLink } from './telas/Acesso';
import { Acervo } from './telas/Acervo';
import { OrdemDeServico } from './telas/Os';
import { Mapa } from './telas/Mapa';
import { Autenticidade } from './telas/Autenticidade';

/* 317 · o relatório interativo CPTu desce só quando alguém o abre: o motor e os desenhos não pesam na lista de OS. */
const RelatorioCptu = lazy(() => import('./telas/cptu/RelatorioCptu').then((m) => ({ default: m.RelatorioCptu })));
const EntradaPeloToken = lazy(() => import('./telas/cptu/RelatorioCptu').then((m) => ({ default: m.EntradaPeloToken })));
const carregandoOCptu = <p className="sub" role="status">Carregando o relatório interativo…</p>;
import { aplicarTema, lerTema } from './tema';
import { QuemEsta } from './quem';
import { BotaoDeTema } from './BotaoDeTema';
import './estilo.css';

interface Eu { id?: string | null; nome: string | null; email: string | null }

/**
 * O portal do cliente.
 *
 * Quatro telas e uma pergunta em todas: onde está o documento que eu vim
 * buscar. Não há painel, não há indicadores, não há menu lateral — o
 * cliente entra três ou quatro vezes por ano e não vai aprender uma
 * navegação.
 *
 * O envelope da tela é a faixa-título do documento controlado do SGQ: a
 * mesma que ele já vê no papel que recebe por e-mail. Quem abre o portal
 * reconhece a Testesolo antes de ler uma palavra.
 */

function Aplicacao() {
  const [eu, setEu] = useState<Eu | null>(null);
  const [pronto, setPronto] = useState(false);
  const navegar = useNavigate();
  /* 220 · o mapa usa a largura inteira, como na bancada (215). */
  const onde = useLocation().pathname;

  const perguntarQuemSou = useCallback(async () => {
    try {
      setEu(await api<Eu>('/eu'));
    } catch {
      setEu(null);
    } finally {
      setPronto(true);
    }
  }, []);

  useEffect(() => { void perguntarQuemSou(); }, [perguntarQuemSou]);

  /*
   * Quando a sessão cai no meio de uma navegação, a volta é para a tela
   * de entrada — e não uma mensagem de erro sobre uma lista vazia.
   */
  useEffect(() => aoPerderSessao(() => {
    setEu(null);
    navegar('/', { replace: true });
  }), [navegar]);

  const sair = async () => {
    await api('/sair', { metodo: 'POST' }).catch(() => { /* já caiu */ });
    setEu(null);
    navegar('/', { replace: true });
  };

  if (!pronto) {
    return (
      <>
        <header className="faixa">
          <div className="faixa-interna">
            <Marca />
            <div className="cresce" />
            <BotaoDeTema />
          </div>
        </header>
        <main className="corpo"><p className="sub">Carregando…</p></main>
      </>
    );
  }

  if (!eu?.email) {
    /*
     * Quem chega pelo link do e-mail traz o segredo no fragmento do
     * endereço, e não digita nada. A leitura já aconteceu no
     * carregamento do módulo — aqui só se decide qual das duas portas
     * mostrar.
     */
    if (segredoDoLink) {
      return (
        <Acesso
          segredo={segredoDoLink}
          aoEntrar={() => { void perguntarQuemSou(); }}
        />
      );
    }
    return <Entrar aoEntrar={() => { void perguntarQuemSou(); }} />;
  }

  return (
    <>
      <header className="faixa">
        <div className="faixa-interna">
          <Marca />
          <div className="cresce" />
          {/*
            * O bloco à direita da faixa é o «de quem é este documento»,
            * exatamente como no bloco de identificação do papel.
            */}
          <div className="faixa-quem">
            <strong>{eu.nome ?? eu.email}</strong>
            <span>{eu.nome ? eu.email : null}</span>
          </div>
          <BotaoDeTema />
          <button type="button" className="botao-faixa" onClick={sair}>Sair</button>
        </div>
      </header>

      <nav className="abas" aria-label="Seções do portal">
        <div className="abas-internas">
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'aba atual' : 'aba')}>
            Ordens de serviço
          </NavLink>
          <NavLink to="/mapa" className={({ isActive }) => (isActive ? 'aba atual' : 'aba')}>
            Mapa
          </NavLink>
        </div>
      </nav>

      {/* 317 · o relatório interativo CPTu também usa a largura inteira (os perfis lado a lado, o corte e o 3D). */}
      <main className={`corpo${onde === '/mapa' ? ' larga' : onde.startsWith('/cptu/') ? ' larga' : ''}`}>
        {/* Só o id: o e-mail no nome da chave do aparelho seria dado pessoal guardado à toa (LGPD). Sem id, nada se guarda. */}
        <QuemEsta.Provider value={eu.id ?? null}>
        <Routes>
          <Route path="/" element={<Acervo />} />
          <Route path="/mapa" element={<Mapa />} />
          <Route path="/os/:id" element={<OrdemDeServico />} />
          {/* 317 · o relatório interativo CPTu: pelo QR (o token) e pela OS. */}
          <Route path="/cptu/r/:token" element={<Suspense fallback={carregandoOCptu}><EntradaPeloToken /></Suspense>} />
          <Route path="/cptu/:id" element={<Suspense fallback={carregandoOCptu}><RelatorioCptu /></Suspense>} />
          <Route path="/cptu/:id/:vista" element={<Suspense fallback={carregandoOCptu}><RelatorioCptu /></Suspense>} />
          {/* Endereço desconhecido volta para a lista, nunca página em branco. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </QuemEsta.Provider>
      </main>

      <footer className="controle">
        <div className="controle-interno">
          <p>
            Documentos controlados do Sistema de Gestão da Qualidade. A
            revisão mais recente de cada documento é a que vale, e é a que
            aparece aqui.
          </p>
          <p>
            Testesolo Engenharia Geotécnica · Blumenau/SC · (47) 3037-2817
          </p>
        </div>
      </footer>
    </>
  );
}

/*
 * O tema é aplicado ANTES do primeiro desenho.
 *
 * Se ele fosse aplicado dentro de um `useEffect`, a página abriria
 * clara por um quadro e escureceria depois — a piscada branca que
 * incomoda justamente quem escolheu o tema escuro, e no escuro.
 */
aplicarTema(lerTema());

/* O alternador de tema mora em `BotaoDeTema.tsx` desde a onda 284: ele
   aparece também na entrada, que não passa por aqui. */

createRoot(document.getElementById('raiz')!).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        {/*
          * 315 · a autenticidade do relatório (R8). Pública e FORA da
          * entrada por código: quem aponta a câmera para o QR da folha
          * impressa não tem conta, e a página não pergunta `/eu`. O
          * endereço é curto de propósito — vai impresso embaixo do QR.
          */}
        <Route path="/r/:token" element={<Autenticidade />} />
        <Route path="*" element={<Aplicacao />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
);
